import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import {
  Box,
  Button,
  MenuItem,
  Select,
  Slider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { AstroAngle } from "../../../../models/interfaces";
import astrocartographyStyles from "../Astrocartography.styles";
import {
  ANGLE_LABEL_KEYS,
  ANGLE_OPTIONS,
  ANY_ANGLE,
  FALLBACK_LINE_COLOR,
  RADIUS_MAX_KM,
  RADIUS_MIN_KM,
  RADIUS_STEP_KM,
} from "../utils/astrocartographyConstants";
import { getPlanetGlyph } from "../utils/astrocartographyUtils";

interface AstroCitySearchControlProps {
  planets: string[];
  planetColors: Record<string, string>;
  selectedPlanet: string;
  onPlanetChange: (planet: string) => void;
  selectedAngle: string;
  onAngleChange: (angle: string) => void;
  radiusKm: number;
  onRadiusChange: (radius: number) => void;
  onFindCities: () => void;
  onClearCities: () => void;
  isSearchDisabled: boolean;
}

/** Short codes for the segmented angle control; full meanings live in tooltips. */
const ANGLE_SHORT_KEYS: Record<string, string> = {
  [ANY_ANGLE]: "astroAnyLineCode",
  MC: "astroAngleMcCode",
  IC: "astroAngleIcCode",
  ASC: "astroAngleAscCode",
  DSC: "astroAngleDscCode",
};

const AstroCitySearchControl = (props: AstroCitySearchControlProps) => {
  const classes = astrocartographyStyles;
  const { t } = useTranslation();
  const {
    planets,
    planetColors,
    selectedPlanet,
    onPlanetChange,
    selectedAngle,
    onAngleChange,
    radiusKm,
    onRadiusChange,
    onFindCities,
    onClearCities,
    isSearchDisabled,
  } = props;

  const angleValues: string[] = [ANY_ANGLE, ...ANGLE_OPTIONS];

  const getColorDot = (color: string) => (
    <Box
      sx={{
        width: 9,
        height: 9,
        flexShrink: 0,
        borderRadius: "50%",
        bgcolor: color,
        boxShadow: `0 0 8px ${color}`,
      }}
    />
  );

  return (
    <Stack spacing={1.75}>
      <Box>
        <Typography variant="overline" sx={classes.fieldLabel}>
          {t("astroPlanet")}
        </Typography>
        <Select
          fullWidth
          value={planets.includes(selectedPlanet) ? selectedPlanet : ""}
          onChange={(event) => onPlanetChange(event.target.value as string)}
          displayEmpty
          disabled={!planets.length}
        >
          {!planets.length && (
            <MenuItem value="">{t("astroNoPlanetsAvailable")}</MenuItem>
          )}
          {planets.map((planet) => (
            <MenuItem key={planet} value={planet}>
              {getColorDot(planetColors[planet] ?? FALLBACK_LINE_COLOR)}
              {`${getPlanetGlyph(planet)} ${planet}`.trim()}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Box>
        <Typography variant="overline" sx={classes.fieldLabel}>
          {t("astroLine")}
        </Typography>
        <ToggleButtonGroup
          exclusive
          value={selectedAngle}
          onChange={(event, value) => value && onAngleChange(value)}
          sx={classes.angleGroup}
        >
          {angleValues.map((angle) => (
            <ToggleButton key={angle} value={angle}>
              <Tooltip
                title={
                  angle === ANY_ANGLE
                    ? t("astroAnyLine")
                    : t(ANGLE_LABEL_KEYS[angle as AstroAngle])
                }
              >
                <Box component="span">{t(ANGLE_SHORT_KEYS[angle])}</Box>
              </Tooltip>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Box>
        <Typography variant="overline" sx={classes.fieldLabel} component="div">
          <Box component="span">{t("astroWithin")}</Box>
          <Box component="span" sx={{ color: "var(--astro-accent)" }}>
            {t("astroKilometres", { distance: radiusKm })}
          </Box>
        </Typography>
        <Slider
          value={radiusKm}
          min={RADIUS_MIN_KM}
          max={RADIUS_MAX_KM}
          step={RADIUS_STEP_KM}
          onChange={(event, value) => onRadiusChange(value as number)}
          aria-label={t("astroWithin")}
        />
      </Box>

      <Stack spacing={1}>
        <Button
          fullWidth
          variant="contained"
          onClick={onFindCities}
          disabled={isSearchDisabled}
          startIcon={<TravelExploreOutlinedIcon />}
        >
          {t("astroFindCities")}
        </Button>
        <Button fullWidth variant="text" onClick={onClearCities}>
          {t("astroClear")}
        </Button>
      </Stack>
    </Stack>
  );
};

export default AstroCitySearchControl;
